import { useRef, type KeyboardEvent } from 'react';

export type VerticalTabItem = { key: string; label: string };

// Left-rail vertical tab list. Controlled: the parent owns the active key.
// WAI-ARIA tabs pattern with vertical orientation (Up/Down/Home/End). Pair each
// panel with role="tabpanel" id={`tabpanel-${key}`}.
export function VerticalTabs({
  tabs,
  active,
  onSelect,
  ariaLabel,
}: {
  tabs: readonly VerticalTabItem[]; // readonly so an `as const` tab list is accepted
  active: string;
  onSelect: (key: string) => void;
  ariaLabel?: string;
}) {
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const keys = tabs.map((t) => t.key);
    const i = keys.indexOf(active);
    if (i < 0) return;
    let next = i;
    if (e.key === 'ArrowDown') next = (i + 1) % keys.length;
    else if (e.key === 'ArrowUp') next = (i - 1 + keys.length) % keys.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = keys.length - 1;
    else return;
    e.preventDefault();
    const nextKey = keys[next]!;
    onSelect(nextKey);
    btnRefs.current[nextKey]?.focus();
  }

  return (
    <div
      className="flex w-56 shrink-0 flex-col divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white"
      role="tablist"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            ref={(el) => {
              btnRefs.current[t.key] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${t.key}`}
            aria-controls={`tabpanel-${t.key}`}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(t.key)}
            className={`px-4 py-3 text-left text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
              isActive
                ? 'bg-blue-50 font-medium text-blue-700'
                : 'font-medium text-slate-600 hover:bg-slate-50'
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
